import { catchError, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, Subscription, throwError } from 'rxjs';
import { OnDestroy, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';

import { IPagination } from '../list/pagination.interface';
import { UrlBuilder } from '../api/url-builder';
import { Parameters } from '../list/parameters.model';
import { IConfirmationDialogPayload } from '../../ui/component/confirmation-dialog/confirmation-dialog-payload.interface';

export abstract class AbstractListComponent implements OnInit, OnDestroy {
    protected filtersVisible: boolean = false;
    protected loading: boolean = false;
    protected loadingPromise: Subscription;
    protected routeChangeSubscription: Subscription;
    protected pagination: IPagination = <IPagination>{ rows: [], count: 0 };
    protected searchParameters: any = {};
    protected parameters: Parameters = new Parameters();

    protected currentPreset: any;
    protected defaultSubject: Subject<IConfirmationDialogPayload> = new Subject();
    protected deleteSubject: Subject<IConfirmationDialogPayload> = new Subject();

    protected abstract getResourceUrl(): string;

    constructor(
        protected httpClient: HttpClient,
        protected toastrService: ToastrService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected urlBuilder?: UrlBuilder
    ) {}

    ngOnInit(): void {
        this.load();
    }

    ngOnDestroy() {
        this.deleteSubject.complete();
        if (this.loadingPromise) {
            this.loadingPromise.unsubscribe();
        }
    }

    getSearchConditions() {
        const conditionsTransformed: any = {};
        Object.keys(this.searchParameters).forEach((key) => {
            if (moment.isDate(this.searchParameters[key])) {
                conditionsTransformed[key] = moment(this.searchParameters[key]).format('YYYY-MM-DD');
            } else {
                conditionsTransformed[key] = this.searchParameters[key];
            }
        });
        return _.pickBy(conditionsTransformed, (param: any) => {
            if (Array.isArray(param)) {
                param = param.filter((val: any) => !!val);
            }
            return param && param !== 'null' && param.length > 0;
        });
    }

    search(): void {
        this.parameters.conditions = this.getSearchConditions();
        this.load();
    }

    load(): Subscription {
        this.loading = true;
        if (this.loadingPromise) {
            this.loadingPromise.unsubscribe();
        }
        this.loadingPromise = this.fetchRemoteSource().subscribe();
        this.loadingPromise.add(() => {
            if (this.pagination.count <= (this.parameters.limit || 999) && this.parameters.offset > 1) {
                this.parameters.offset = 1;
            }
            this.loading = false;
        });
        return this.loadingPromise;
    }

    buildUrl() {
        let url = this.getResourceUrl();
        if (this.urlBuilder) {
            url = this.urlBuilder.buildUrl(url);
        }

        return url;
    }

    fetchRemoteSource(): Observable<any> {
        return this.httpClient.get<any>(this.buildUrl(), { params: this.parameters.toStringParams() }).pipe(
            map((response: any) => {
                this.pagination = <IPagination>response;
                console.log(response)
                return response;
            }),
            catchError((error: any) => {
                this.toastrService.error(error.error ? error.error.message || 'Something went wrong...' : 'Error');
                return throwError(error);
            })
        );
    }

    delete(id): any {
        if (!id) {
            return setTimeout(() => {
                this.deleteSubject.next(<IConfirmationDialogPayload>{
                    result: true,
                    message: 'Deleted',
                });
                return this.load();
            }, 1);
        }
        return this.httpClient.delete<any>(this.buildUrl() + '/' + id).subscribe(
            (response: any) => {
                this.deleteSubject.next(<IConfirmationDialogPayload>{
                    result: true,
                    message: 'Deleted',
                });

                this.load();

                return response;
            },
            (error: any) => {
                this.toastrService.error(error.error ? error.error.message || 'Something went wrong...' : 'Error');
                this.deleteSubject.next(<IConfirmationDialogPayload>{
                    result: false,
                    message: 'Unable to delete',
                });

                return error;
            }
        );
    }

    setPage(pageInfo: any) {
        if (this.parameters.offset === pageInfo.offset) {
            return;
        }
        this.parameters.offset = pageInfo.offset;
        this.load();
    }

    sort($event) {
        this.parameters.orderBy = $event.sorts[0].prop;
        this.parameters.orderDir = $event.sorts[0].dir;
        this.load();
    }

    makeActive(id: number): void {
        this.httpClient
            .put(this.urlBuilder.buildUrl(this.getResourceUrl() + '/custom/' + id + '/active'), { isActive: true })
            .subscribe(
                (response: any) => {
                    this.load();
                },
                (error: any) => {
                    this.loading = false;
                }
            );
    }

    makeInactive(id: number): void {
        this.httpClient
            .put(this.urlBuilder.buildUrl(this.getResourceUrl() + '/custom/' + id + '/active'), { isActive: false })
            .subscribe(
                (response: any) => {
                    this.load();
                },
                (error: any) => {
                    this.loading = false;
                }
            );
    }

    protected resetPaginationObject(): void {
        this.pagination = <IPagination>{ rows: [], count: 0 };
    }
}
